/**
 * IssueSlideOut — Right panel overlay showing real execution data
 *
 * Sections:
 * 1. Issue header (status, duration, epic context)
 * 2. ExecutionDetails (result banner, errors, commits, tests, files, output, timeline)
 */

import { useState } from 'react';
import {
  Radio,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Info,
  Clock,
  GitCommit,
  FileText,
  TestTube2,
  ChevronDown,
  ChevronRight,
  Terminal,
} from 'lucide-react';
import { RightPanel } from '../layout/RightPanel';
import { useIssueTelemetry, useSprintTelemetry as useSprintTelemetryFallback } from '../../hooks/useIssueEvidence';
import type { TelemetryEvent } from '../../api/telemetry';
import type { SprintEpic } from '../../api/sprint';
import type { WaterfallIssue } from './SprintWaterfall';
import { SpriteActivityFeed } from '../sprint/SpriteActivityFeed';

interface IssueSlideOutProps {
  issue: WaterfallIssue | null;
  epic?: SprintEpic;
  readinessScore?: number;
  workflowId?: string;
  batchId?: string;
  onClose: () => void;
}

export function IssueSlideOut({ issue, epic, readinessScore, workflowId, batchId, onClose }: IssueSlideOutProps) {
  return (
    <RightPanel
      isOpen={!!issue}
      onClose={onClose}
      mode="overlay"
      width={480}
      title=""
      showHeader={false}
    >
      {issue && (
        <div className="flex flex-col h-full overflow-y-auto">
          <IssueHeader issue={issue} epic={epic} readinessScore={readinessScore} onClose={onClose} />
          <div className="border-t border-neutral-100 flex-1">
            <ExecutionDetails issueId={issue.id} workflowId={workflowId} />
            {/* Per-story sprite activity */}
            {batchId && (
              <div className="px-4 pb-4">
                <ActivitySection issueId={issue.id} batchId={batchId} />
              </div>
            )}
          </div>
        </div>
      )}
    </RightPanel>
  );
}

/* ── Issue Header ─────────────────────────────────────────── */

const statusColors: Record<string, { bg: string; text: string }> = {
  pending:   { bg: 'bg-neutral-100', text: 'text-neutral-600' },
  building:  { bg: 'bg-coral-100', text: 'text-coral-700' },
  completed: { bg: 'bg-accent-100', text: 'text-accent-700' },
  failed:    { bg: 'bg-red-100', text: 'text-red-700' },
};

function IssueHeader({ issue, epic, readinessScore, onClose }: {
  issue: WaterfallIssue;
  epic?: SprintEpic;
  readinessScore?: number;
  onClose: () => void;
}) {
  const sc = statusColors[issue.status] || statusColors.pending;

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-semibold text-neutral-800">{issue.label}</p>
          <p className="text-[10px] text-neutral-400 font-mono mt-0.5">{issue.id}</p>
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-neutral-100 rounded transition-colors text-neutral-400 hover:text-neutral-600"
        >
          <XCircle size={16} />
        </button>
      </div>

      <div className="flex items-center gap-3">
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${sc.bg} ${sc.text}`}>
          {issue.status === 'building' && <Radio size={12} className="animate-pulse" />}
          {issue.status === 'completed' && <CheckCircle2 size={12} />}
          {issue.status === 'failed' && <XCircle size={12} />}
          {issue.status.charAt(0).toUpperCase() + issue.status.slice(1)}
        </span>
        <span className="text-xs font-mono tabular-nums text-neutral-500">
          {Math.round(issue.duration / 60_000)}m
        </span>
      </div>

      {epic && (
        <div className="flex items-center gap-2 text-xs text-neutral-600">
          <span className={`w-2 h-2 rounded-full ${
            epic.status === 'completed' ? 'bg-accent-500' :
            epic.status === 'building' ? 'bg-coral-500' :
            epic.status === 'failed' ? 'bg-red-500' : 'bg-neutral-300'
          }`} />
          <span>{epic.epicId}</span>
          {readinessScore != null && (
            <span className={`font-semibold ${
              readinessScore >= 80 ? 'text-accent-600' :
              readinessScore >= 50 ? 'text-amber-600' : 'text-red-600'
            }`}>
              {readinessScore}% ready
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Execution Details ────────────────────────────────────── */

interface CommitData {
  sha: string;
  message: string;
  files?: string[];
}

interface TestResultData {
  passed?: number;
  failed?: number;
  skipped?: number;
  total?: number;
}

function ExecutionDetails({ issueId, workflowId }: { issueId: string; workflowId?: string }) {
  const issueTelemetry = useIssueTelemetry(issueId);
  const sprintTelemetry = useSprintTelemetryFallback(
    (issueTelemetry.data?.events?.length === 0 && workflowId) ? workflowId : undefined
  );

  const isLoading = issueTelemetry.isLoading || sprintTelemetry.isLoading;
  const rawEvents: TelemetryEvent[] = (issueTelemetry.data?.events?.length ?? 0) > 0
    ? issueTelemetry.data!.events
    : sprintTelemetry.data?.events || [];

  // Filter out heartbeat spam for timeline display
  const allEvents = rawEvents.filter(e => e.eventType !== 'sprite_heartbeat');

  // Extract execution result from the most recent story_execution_completed
  const completionEvent = rawEvents.find(e => e.eventType === 'story_execution_completed');
  const payload = completionEvent?.payload as Record<string, unknown> | null;

  const resultStatus = (payload?.status as string) || completionEvent?.severity || null;
  const totalDuration = (payload?.duration as number) ?? completionEvent?.durationMs ?? null;
  const errorMessage = (payload?.errorMessage as string) || completionEvent?.errorMessage || null;
  const outputExcerpt = (payload?.outputExcerpt as string) || null;
  const files = (payload?.files as string[]) || [];
  const filesChangedCount = (payload?.filesChanged as number) || 0;
  const testResults = (payload?.testResults as TestResultData) || null;

  // Collect commits from payload or from commit_made events
  const payloadCommits = (payload?.commits as CommitData[]) || [];
  const commitEvents = allEvents.filter(e => e.eventType === 'commit_made');
  const commits: CommitData[] = payloadCommits.length > 0
    ? payloadCommits
    : commitEvents.map(e => ({
        sha: (e.payload?.sha as string) || (e.payload?.commitSha as string) || '',
        message: (e.payload?.message as string) || (e.payload?.commitMessage as string) || '',
        files: (e.payload?.files as string[]) || (e.payload?.changedFiles as string[]) || [],
      }));

  const fileCount = files.length || filesChangedCount || commits.reduce((n, c) => n + (c.files?.length || 0), 0);
  const commitCount = commits.length;

  if (isLoading) {
    return (
      <div className="p-4">
        <p className="text-[10px] text-neutral-400">Loading execution data...</p>
      </div>
    );
  }

  if (allEvents.length === 0) {
    return (
      <div className="p-4">
        <div className="h-16 flex items-center justify-center border-2 border-dashed border-neutral-200 rounded-lg">
          <p className="text-[10px] text-neutral-400">No telemetry events recorded</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Result Banner */}
      {resultStatus && (
        <ResultBanner
          status={resultStatus}
          durationMs={totalDuration}
          fileCount={fileCount}
          commitCount={commitCount}
        />
      )}

      {/* Error Block */}
      {errorMessage && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <XCircle size={14} className="text-red-500 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-widest text-red-600 mb-1">Error</p>
              <p className="text-[11px] text-red-700 font-mono whitespace-pre-wrap break-words leading-relaxed">
                {errorMessage}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Commits */}
      {commits.length > 0 && (
        <Section icon={GitCommit} title="Commits" count={commits.length}>
          <div className="space-y-2">
            {commits.map((commit, i) => (
              <div key={commit.sha || i} className="bg-neutral-50 rounded-lg p-2.5">
                <div className="flex items-center gap-2">
                  {commit.sha && (
                    <span className="text-[10px] font-mono text-coral-600 bg-coral-50 px-1.5 py-0.5 rounded shrink-0">
                      {commit.sha.slice(0, 7)}
                    </span>
                  )}
                  <span className="text-[11px] text-neutral-700 truncate">{commit.message || 'No message'}</span>
                </div>
                {commit.files && commit.files.length > 0 && (
                  <div className="mt-1.5 space-y-0.5">
                    {commit.files.slice(0, 10).map((f) => (
                      <p key={f} className="text-[9px] font-mono text-neutral-400 truncate pl-2 border-l-2 border-neutral-200">
                        {f}
                      </p>
                    ))}
                    {commit.files.length > 10 && (
                      <p className="text-[9px] text-neutral-400 pl-2">+{commit.files.length - 10} more</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Test Results */}
      {testResults && (testResults.passed != null || testResults.failed != null) && (
        <Section icon={TestTube2} title="Test Results">
          <div className="flex items-center gap-3">
            {testResults.passed != null && (
              <span className="flex items-center gap-1 text-[11px]">
                <CheckCircle2 size={12} className="text-accent-500" />
                <span className="font-mono font-semibold text-accent-700">{testResults.passed}</span>
                <span className="text-neutral-400">passed</span>
              </span>
            )}
            {testResults.failed != null && testResults.failed > 0 && (
              <span className="flex items-center gap-1 text-[11px]">
                <XCircle size={12} className="text-red-500" />
                <span className="font-mono font-semibold text-red-700">{testResults.failed}</span>
                <span className="text-neutral-400">failed</span>
              </span>
            )}
            {testResults.skipped != null && testResults.skipped > 0 && (
              <span className="flex items-center gap-1 text-[11px]">
                <AlertTriangle size={12} className="text-amber-500" />
                <span className="font-mono font-semibold text-amber-700">{testResults.skipped}</span>
                <span className="text-neutral-400">skipped</span>
              </span>
            )}
          </div>
        </Section>
      )}

      {/* Files Changed */}
      {files.length > 0 && (
        <Section icon={FileText} title="Files Changed" count={files.length}>
          <div className="max-h-[160px] overflow-y-auto space-y-0.5">
            {files.map((f) => (
              <p key={f} className="text-[10px] font-mono text-neutral-500 truncate py-0.5 px-2 rounded hover:bg-neutral-100">
                {f}
              </p>
            ))}
          </div>
        </Section>
      )}

      {/* Output Excerpt */}
      {outputExcerpt && <OutputExcerpt text={outputExcerpt} />}

      {/* Event Timeline */}
      <EventTimeline events={allEvents} />
    </div>
  );
}

/* ── Sub-components ───────────────────────────────────────── */

function ResultBanner({ status, durationMs, fileCount, commitCount }: {
  status: string;
  durationMs: number | null;
  fileCount: number;
  commitCount: number;
}) {
  const isSuccess = status === 'success' || status === 'completed' || status === 'info';
  const isFailed = status === 'failure' || status === 'failed' || status === 'error';
  const bg = isSuccess ? 'bg-accent-50 border-accent-200' : isFailed ? 'bg-red-50 border-red-200' : 'bg-neutral-50 border-neutral-200';
  const textColor = isSuccess ? 'text-accent-700' : isFailed ? 'text-red-700' : 'text-neutral-700';

  return (
    <div className={`rounded-lg border p-3 ${bg}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <span className={`text-xs font-bold uppercase tracking-wider ${textColor}`}>
          {isSuccess ? 'Completed' : isFailed ? 'Failed' : status}
        </span>
        {durationMs != null && (
          <span className="flex items-center gap-1 text-[11px] text-neutral-500">
            <Clock size={11} />
            <span className="font-mono tabular-nums">
              {durationMs >= 60_000 ? `${Math.round(durationMs / 60_000)}m` : `${Math.round(durationMs / 1000)}s`}
            </span>
          </span>
        )}
        {fileCount > 0 && (
          <span className="flex items-center gap-1 text-[11px] text-neutral-500">
            <FileText size={11} />
            <span className="font-mono">{fileCount} files</span>
          </span>
        )}
        {commitCount > 0 && (
          <span className="flex items-center gap-1 text-[11px] text-neutral-500">
            <GitCommit size={11} />
            <span className="font-mono">{commitCount} commits</span>
          </span>
        )}
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, count, children }: {
  icon: typeof Info;
  title: string;
  count?: number;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={13} className="text-neutral-400" />
        <span className="text-[10px] font-bold uppercase tracking-widest text-neutral-500">{title}</span>
        {count != null && (
          <span className="text-[10px] font-mono text-neutral-400">{count}</span>
        )}
      </div>
      {children}
    </div>
  );
}

function OutputExcerpt({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false);
  const preview = text.slice(-500);
  const full = text.slice(-2000);

  return (
    <div>
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 mb-2 group"
      >
        <Terminal size={13} className="text-neutral-400" />
        <span className="text-[10px] font-bold uppercase tracking-widest text-neutral-500">Output</span>
        {expanded ? <ChevronDown size={12} className="text-neutral-400" /> : <ChevronRight size={12} className="text-neutral-400" />}
      </button>
      <div className="bg-neutral-900 rounded-lg p-3 overflow-x-auto">
        <pre className="text-[10px] font-mono text-neutral-300 whitespace-pre-wrap break-words leading-relaxed">
          {expanded ? full : preview}
        </pre>
        {!expanded && text.length > 500 && (
          <button
            onClick={() => setExpanded(true)}
            className="text-[9px] text-coral-400 hover:text-coral-300 mt-1"
          >
            Show more ({Math.min(text.length, 2000)} chars)
          </button>
        )}
      </div>
    </div>
  );
}

const severityIcon: Record<string, { icon: typeof Info; color: string }> = {
  info:     { icon: Info, color: 'text-neutral-400' },
  warning:  { icon: AlertTriangle, color: 'text-amber-500' },
  error:    { icon: XCircle, color: 'text-red-500' },
  critical: { icon: XCircle, color: 'text-red-600' },
};

function eventDetail(event: TelemetryEvent): string {
  const p = event.payload;
  if (!p) return '';
  if (event.eventType === 'commit_made') return (p.sha as string)?.slice(0, 7) || '';
  if (event.eventType === 'story_execution_completed') return (p.status as string) || '';
  if (event.eventType.endsWith('_failed')) return (p.errorMessage as string)?.slice(0, 60) || event.errorMessage?.slice(0, 60) || '';
  if (event.eventType === 'sprite_spawned') return (p.machineId as string) || '';
  return '';
}

function EventTimeline({ events }: { events: TelemetryEvent[] }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Clock size={13} className="text-neutral-400" />
        <span className="text-[10px] font-bold uppercase tracking-widest text-neutral-500">Event Timeline</span>
        <span className="text-[10px] font-mono text-neutral-400">{events.length}</span>
      </div>
      <div className="space-y-0.5 max-h-[220px] overflow-y-auto">
        {events.map((event) => {
          const sev = severityIcon[event.severity] || severityIcon.info;
          const Icon = sev.icon;
          const ts = new Date(event.timestamp);
          const detail = eventDetail(event);
          return (
            <div
              key={event.id}
              className="flex items-center gap-2 py-1 px-1.5 rounded hover:bg-neutral-50 transition-colors"
            >
              <span className="text-[9px] font-mono tabular-nums text-neutral-400 shrink-0 w-[52px]">
                {ts.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </span>
              <Icon size={11} className={`${sev.color} shrink-0`} />
              <span className="text-[9px] font-mono text-neutral-600 truncate flex-1">
                {event.eventType}
              </span>
              {detail && (
                <span className="text-[8px] font-mono text-neutral-400 truncate max-w-[100px] shrink-0">
                  {detail}
                </span>
              )}
              {event.durationMs != null && (
                <span className="text-[8px] font-mono tabular-nums text-neutral-400 shrink-0">
                  {event.durationMs}ms
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Per-story Activity Section ─────────────────────────────── */

function ActivitySection({ issueId, batchId }: { issueId: string; batchId: string }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="mt-3">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 w-full text-left"
      >
        {expanded ? <ChevronDown size={12} className="text-neutral-400" /> : <ChevronRight size={12} className="text-neutral-400" />}
        <span className="text-[9px] font-bold uppercase tracking-widest text-neutral-400">Activity</span>
      </button>
      {expanded && (
        <div className="mt-2">
          <SpriteActivityFeed
            batchId={batchId}
            filterId={issueId}
            filterField="issue_id"
            maxHeight="200px"
            compact
          />
        </div>
      )}
    </div>
  );
}

export default IssueSlideOut;
